import type { AioShopperInfo, CoBuyer, VehicleInsurance } from '@makemydeal/dr-platform-types';
import { resolvePrimaryPhone } from '@makemydeal/dr-shared-ui-utils/phone/resolvePhone';
import type { CustomerFormValues } from '../types/CustomerCardTypes';
import type { CustomerSpdSaveItem } from '../types/customerSpd.types';

/**
 * Full payload for POST /api/deal/{dealXgId}/participants.
 * Consolidated save only: `spd` when dirty, `vehicleInsurance` always unless empty.
 */
export interface SaveParticipantsPayload {
    buyer?: Partial<AioShopperInfo>;
    coBuyer?: CoBuyer;
    dealerUser?: string;
    spd?: CustomerSpdSaveItem[];
    vehicleInsurance?: VehicleInsurance;
}

const resolveDealPhone = (values: CustomerFormValues): { phone?: string } => {
    const { cellPhone, homePhone, workPhone } = values;
    if (cellPhone === undefined && homePhone === undefined && workPhone === undefined) return {};
    return { phone: resolvePrimaryPhone({ cellPhone, homePhone, workPhone }) };
};

const toDxgParticipant = (values: CustomerFormValues, enableCorrectPhoneHandling?: boolean): Partial<AioShopperInfo> =>
    ({
        ...values,
        ...(enableCorrectPhoneHandling && resolveDealPhone(values))
    } as Partial<AioShopperInfo>);

/** Maps a CustomerFormValues snapshot into the Partial<AioShopperInfo> shape expected by the BFF save-participants handler for the buyer. */
export const toBuyerData = (values: CustomerFormValues, enableCorrectPhoneHandling?: boolean): Partial<AioShopperInfo> => {
    const { business, bankAccountNumber } = values;

    return {
        ...toDxgParticipant(values, enableCorrectPhoneHandling),
        ...(business !== undefined && {
            business: {
                ...(business as AioShopperInfo['business']),
                ...(bankAccountNumber && { bankAccountNumber })
            } as AioShopperInfo['business']
        })
    };
};

/** Maps a CustomerFormValues snapshot into the CoBuyer shape expected by the BFF save-participants handler for the co-buyer. */
export const toCoBuyerData = (values: CustomerFormValues, enableCorrectPhoneHandling?: boolean): CoBuyer =>
    toDxgParticipant(values, enableCorrectPhoneHandling) as CoBuyer;

/**
 * Builds the full SaveParticipantsPayload from buyer + optional co-buyer form values.
 * dealId is passed in the URL path — it is NOT included in the request body.
 */
export const buildParticipantsPayload = (
    buyer: CustomerFormValues,
    coBuyer: CustomerFormValues | null | undefined,
    dealerUser?: string,
    enableCorrectPhoneHandling?: boolean
): SaveParticipantsPayload => ({
    buyer: toBuyerData(buyer, enableCorrectPhoneHandling),
    ...(coBuyer && { coBuyer: toCoBuyerData(coBuyer, enableCorrectPhoneHandling) }),
    ...(dealerUser && { dealerUser })
});
